<script setup>
import { cloneVNode, computed, h, isVNode, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { vOnClickOutside } from '@vueuse/components';
import { useEventListener } from '@vueuse/core';

import Icon from 'next/icon/Icon.vue';
import { useSidebarContext } from './provider';
import { matchesSidebarActiveRoute } from './managementMenu';

const props = defineProps({
  sections: {
    type: Array,
    default: () => [],
  },
  favorites: {
    type: Array,
    default: () => [],
  },
  boxed: {
    type: Boolean,
    default: false,
  },
});

const route = useRoute();
const store = useStore();
const { isAllowed, resolvePath } = useSidebarContext();
const openMenu = ref(null);
const dropdownStyle = ref({});
const navigationLabel = 'Navegação principal';

const NavigationIcon = ({ icon, className = 'size-4 shrink-0' }) => {
  if (!icon) return null;
  if (typeof icon === 'string') return h(Icon, { icon, class: className });
  if (isVNode(icon)) return cloneVNode(icon, { class: className });
  return h(icon, { class: className });
};

const filterItem = item => {
  if (!item || item.show === false) return null;

  const children = (item.children || []).map(filterItem).filter(Boolean);
  const canNavigate = item.to && isAllowed(item.to);
  if (!canNavigate && children.length === 0) return null;

  return {
    ...item,
    ...(item.children ? { children } : {}),
  };
};

const visibleSections = computed(() => {
  const sections = props.sections
    .map(section => ({
      ...section,
      items: section.items.map(filterItem).filter(Boolean),
    }))
    .filter(section => section.items.length > 0);

  const favorites = props.favorites.map(filterItem).filter(Boolean);
  if (favorites.length) {
    sections.splice(1, 0, {
      name: 'section-horizontal-favorites',
      label: 'Favoritos',
      items: favorites,
    });
  }

  return sections;
});

const flattenLeaves = (items, trail = []) =>
  items.flatMap(item => {
    const nextTrail = item.children ? [...trail, item.label] : trail;
    if (item.children?.length) return flattenLeaves(item.children, nextTrail);
    if (!item.to) return [];

    return [{ ...item, trail }];
  });

const dropdownItems = source =>
  flattenLeaves(Array.isArray(source) ? source : source.children || []);

const isItemActive = item => {
  if (item.to) {
    const itemPath = resolvePath(item.to);
    if (route.path === itemPath || route.path.startsWith(`${itemPath}/`)) {
      return true;
    }
    if (matchesSidebarActiveRoute(item, route)) return true;
  }

  return item.children?.some(isItemActive) || false;
};

const badgeCount = item => {
  if (item.badgeCount) return item.badgeCount;
  const countGetter = item.getterKeys?.count;
  return countGetter ? store.getters[countGetter] || 0 : 0;
};

const menuKey = (type, name) => `${type}:${name}`;
const isMenuOpen = (type, name) => openMenu.value === menuKey(type, name);
const positionDropdown = (target, type) => {
  const rect = target.getBoundingClientRect();
  const viewportPadding = 16;
  const preferredWidth = type === 'item' ? 672 : 736;
  const width = Math.min(
    preferredWidth,
    window.innerWidth - viewportPadding * 2
  );
  const left = Math.min(
    Math.max(rect.left, viewportPadding),
    window.innerWidth - width - viewportPadding
  );

  dropdownStyle.value = {
    top: `${rect.bottom + 8}px`,
    left: `${left}px`,
    width: `${width}px`,
  };
};

const toggleMenu = (type, name, event) => {
  const key = menuKey(type, name);
  if (openMenu.value === key) {
    openMenu.value = null;
    return;
  }

  positionDropdown(event.currentTarget, type);
  openMenu.value = key;
};
const closeMenus = () => {
  openMenu.value = null;
};

watch(() => route.fullPath, closeMenus);
useEventListener(document, 'keydown', event => {
  if (event.key === 'Escape') closeMenus();
});
</script>

<template>
  <nav
    class="relative z-30 border-t border-n-weak bg-n-background py-1"
    :aria-label="navigationLabel"
  >
    <div
      v-on-click-outside="closeMenus"
      class="horizontal-navigation-scroll relative mx-auto flex h-12 items-center gap-1 overflow-x-auto px-4"
      :class="boxed ? 'max-w-[1440px]' : 'w-full'"
      @scroll="closeMenus"
    >
      <template v-for="section in visibleSections" :key="section.name">
        <template v-if="section.name === 'section-menu'">
          <div
            v-for="item in section.items"
            :key="item.name"
            class="relative shrink-0"
          >
            <RouterLink
              v-if="item.to && !item.children"
              :to="item.to"
              class="horizontal-navigation-item"
              :class="
                isItemActive(item)
                  ? 'bg-n-brand/10 text-n-brand'
                  : 'text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12'
              "
            >
              <NavigationIcon :icon="item.icon" />
              <span>{{ item.label }}</span>
              <span
                v-if="badgeCount(item)"
                class="min-w-5 rounded-full bg-n-brand px-1.5 py-0.5 text-center text-[10px] font-semibold text-white"
              >
                {{ badgeCount(item) }}
              </span>
            </RouterLink>
            <button
              v-else
              type="button"
              class="horizontal-navigation-item"
              :class="
                isItemActive(item)
                  ? 'bg-n-brand/10 text-n-brand'
                  : 'text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12'
              "
              :aria-expanded="isMenuOpen('item', item.name)"
              @click="toggleMenu('item', item.name, $event)"
            >
              <NavigationIcon :icon="item.icon" />
              <span>{{ item.label }}</span>
              <Icon
                icon="i-hugeicons-arrow-down-01"
                class="size-3.5 transition-transform"
                :class="isMenuOpen('item', item.name) && 'rotate-180'"
              />
            </button>
            <div
              v-if="isMenuOpen('item', item.name)"
              class="fixed z-50 overflow-hidden rounded-2xl border border-n-weak bg-n-solid-1 p-2 shadow-2xl"
              :style="dropdownStyle"
            >
              <div
                class="grid max-h-[min(28rem,calc(100vh-10rem))] grid-cols-2 gap-1 overflow-y-auto"
              >
                <RouterLink
                  v-for="child in dropdownItems(item)"
                  :key="`${item.name}-${child.name}`"
                  :to="child.to"
                  class="flex min-w-0 items-center gap-2 rounded-xl border border-transparent px-3 py-2.5 transition hover:border-n-weak hover:bg-n-alpha-2"
                  :class="
                    isItemActive(child) &&
                    'border-n-brand/15 bg-n-brand/10 text-n-brand'
                  "
                  @click="closeMenus"
                >
                  <NavigationIcon
                    :icon="child.icon"
                    class-name="size-3.5 shrink-0"
                  />
                  <span class="min-w-0 flex-1">
                    <span class="block truncate text-sm font-medium">{{
                      child.label
                    }}</span>
                    <span
                      v-if="child.trail.length"
                      class="block truncate text-[10px] text-n-slate-9"
                    >
                      {{ child.trail.join(' · ') }}
                    </span>
                  </span>
                  <span
                    v-if="badgeCount(child)"
                    class="min-w-5 rounded-full bg-n-brand px-1.5 py-0.5 text-center text-[10px] font-semibold text-white"
                  >
                    {{ badgeCount(child) }}
                  </span>
                </RouterLink>
              </div>
            </div>
          </div>
        </template>

        <div v-else class="relative shrink-0">
          <RouterLink
            v-if="section.direct && section.to"
            :to="section.to"
            class="horizontal-navigation-item"
            :class="
              isItemActive(section)
                ? 'bg-n-brand/10 text-n-brand'
                : 'text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12'
            "
            @click="closeMenus"
          >
            <NavigationIcon :icon="section.icon" />
            <span>{{ section.label }}</span>
          </RouterLink>
          <button
            v-else
            type="button"
            class="horizontal-navigation-item"
            :class="
              section.items.some(isItemActive)
                ? 'bg-n-brand/10 text-n-brand'
                : 'text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12'
            "
            :aria-expanded="isMenuOpen('section', section.name)"
            @click="toggleMenu('section', section.name, $event)"
          >
            <span>{{ section.label || 'Menu' }}</span>
            <Icon
              icon="i-hugeicons-arrow-down-01"
              class="size-3.5 transition-transform"
              :class="isMenuOpen('section', section.name) && 'rotate-180'"
            />
          </button>
          <div
            v-if="!section.direct && isMenuOpen('section', section.name)"
            class="fixed z-50 overflow-hidden rounded-2xl border border-n-weak bg-n-solid-1 p-2 shadow-2xl"
            :style="dropdownStyle"
          >
            <div
              class="grid max-h-[min(28rem,calc(100vh-10rem))] grid-cols-2 gap-1 overflow-y-auto lg:grid-cols-3"
            >
              <RouterLink
                v-for="item in dropdownItems(section.items)"
                :key="`${section.name}-${item.name}`"
                :to="item.to"
                class="flex min-w-0 items-center gap-2 rounded-xl border border-transparent px-3 py-2.5 transition hover:border-n-weak hover:bg-n-alpha-2"
                :class="
                  isItemActive(item) &&
                  'border-n-brand/15 bg-n-brand/10 text-n-brand'
                "
                @click="closeMenus"
              >
                <span
                  v-if="item.icon"
                  class="grid size-8 shrink-0 place-content-center rounded-lg bg-n-alpha-1 text-n-slate-10"
                >
                  <NavigationIcon :icon="item.icon" class-name="size-4" />
                </span>
                <span class="min-w-0 flex-1">
                  <span class="block truncate text-sm font-medium">{{
                    item.label
                  }}</span>
                  <span
                    v-if="item.trail.length"
                    class="block truncate text-[10px] text-n-slate-9"
                  >
                    {{ item.trail.join(' · ') }}
                  </span>
                </span>
                <span
                  v-if="badgeCount(item)"
                  class="min-w-5 rounded-full bg-n-brand px-1.5 py-0.5 text-center text-[10px] font-semibold text-white"
                >
                  {{ badgeCount(item) }}
                </span>
              </RouterLink>
            </div>
          </div>
        </div>
      </template>
    </div>
  </nav>
</template>

<style scoped>
.horizontal-navigation-scroll {
  scrollbar-color: transparent transparent;
  scrollbar-gutter: stable;
  scrollbar-width: thin;
}

.horizontal-navigation-item {
  @apply inline-flex h-9 min-h-9 shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-xl px-3 text-sm font-medium leading-none transition;
}

.horizontal-navigation-scroll:hover {
  scrollbar-color: rgb(var(--color-border)) transparent;
}

.horizontal-navigation-scroll::-webkit-scrollbar {
  height: 6px;
}

.horizontal-navigation-scroll::-webkit-scrollbar-thumb {
  border-radius: 9999px;
  background: transparent;
}

.horizontal-navigation-scroll:hover::-webkit-scrollbar-thumb {
  background: rgb(var(--color-border));
}
</style>
